<template>
  <div>
    <div class="login">
      <start-logo></start-logo>
      <div v-if="isLogin" class="mt-3">
        <div class="alert alert-light" v-if="user">
          {{ $t('src/pages/login.4573') }}<span class="text-info">{{ user.login }}</span>
        </div>

        <button class="btn btn-dark btn-block" type="button" @click="goHome">
          {{ $t('src/pages/login.9978') }}
        </button>
      </div>
      <form v-else class="form-signin" @submit.prevent="login">
        <div class="pb-2">
          <div class="form-group input-group">
            <div class="input-group-prepend">
              <span class="input-group-text icon icon-login"></span>
            </div>
            <label class="sr-only">{{ $t('src/pages/login.1932') }}</label>
            <input
              type="text"
              name="username"
              class="form-control"
              :class="{ 'is-invalid': $v.username.$error || noAuthorizedError || isForbidden }"
              :placeholder="$t('src/pages/login.5510')"
              v-model="$v.username.$model"
              v-focus
            />
          </div>
          <div class="form-group input-group">
            <div class="input-group-prepend">
              <span class="input-group-text icon icon-password"></span>
            </div>
            <label class="sr-only">{{ $t('src/pages/login.5750') }}</label>
            <input
              type="password"
              name="password"
              class="form-control"
              :class="{ 'is-invalid': $v.pass.$error || noAuthorizedError }"
              :placeholder="$t('src/pages/login.2339')"
              v-model="$v.pass.$model"
            />
          </div>
        </div>
        <div class="mt-2 mb-2">
          <div class="form-control-error" v-show="!$v.username.required && $v.username.$error">
            {{ $t('src/pages/login.1971') }}
          </div>
          <div class="form-control-error" v-show="!$v.pass.required && $v.pass.$error">
            {{ $t('src/pages/login.5222') }}
          </div>
          <div class="form-control-error" v-show="noAuthorizedError">
            {{ $t('src/pages/login.18') }}<br />{{ $t('src/pages/login.9383') }}
          </div>
          <div class="form-control-error" v-show="isForbidden">{{ $t('src/pages/login.8840') }}</div>
        </div>

        <div class="pt-2">
          <div class="form-group text-right">
            <a class="text-info small reset-password" @click="resetPassword">
              {{ $t('src/pages/login.9710') }}
            </a>
          </div>
          <button class="btn btn-lg btn-dark btn-block" type="submit">
            <span v-if="sending" class="spinner-border spinner-border-sm"></span
            >{{ $t('src/pages/login.6716') }}
          </button>
        </div>
      </form>
    </div>
    <base-modal :is-show="isShowNoEmailWarningModal" mode="ok" @close="closeNoEmailWarningModal">
      <template #header>{{ $t('messageEmail.passwordRecovery') }}</template>
      <template #body>{{ textBody }}</template>
    </base-modal>
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { userRights } from '@/mixins/user-rights.js'
import StartLogo from '@/views/StartLogo.vue'
import { notIn } from '@/modules/validators'
import { emailSent } from '@/mixins/email-sent.js'
import { systemUnavailable } from '@/mixins/system-unavailable.js'
import { fullName } from '@/mixins/full-name.js'
import('@/styles/components/login.less')

export default {
  name: 'LoginPage',
  components: {
    StartLogo,
    BaseModal: () => import('@/base/BaseModal.vue')
  },
  mixins: [userRights, emailSent, systemUnavailable, fullName],
  directives: {
    focus: {
      inserted: function(el) {
        el.focus()
      }
    }
  },
  data: () => ({
    username: null,
    pass: null,
    sending: false,
    is401Error: false,
    isForbidden: false,
    excludedLogins: [],
    isShowNoEmailWarningModal: false,
    surnameAdmin: null
  }),
  validations() {
    return {
      pass: {
        required
      },
      username: {
        required,
        notIn: value => notIn(value, this.excludedLogins)
      }
    }
  },
  watch: {
    username() {
      this.clearError()
    }
  },
  created() {
    this.username = this.loginFromRegistration
  },
  computed: {
    user() {
      return this.$store.getters.AUTH_USER
    },
    isLogin() {
      return this.$store.getters.IS_LOGIN
    },
    redirectUrl() {
      return this.$route.query.redirect || this.$route.params.redirect
    },
    noAuthorizedError() {
      return this.is401Error && !this.$v.$anyDirty
    },
    textBody() {
      return this.$t('messageEmail.warningNoEmail', { '1': this.surnameAdmin })
    },
    loginFromRegistration() {
      return this.$route.params.login
    }
  },
  methods: {
    login() {
      this.clearError()
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      this.sending = true
      this.$store
        .dispatch('AUTH_REQUEST', {
          username: this.username,
          password: this.pass
        })
        .then(() => {
          this.$store.dispatch('LOAD_AUTH_USER').then(() => {
            this.redirect()
          })
        })
        .catch(error => {
          if (!error.response) {
            alert(this.$t('errorMessage.serverUnavailable'))
          } else if (error.response.status === 401) {
            this.is401Error = true
          } else if (error.response.status === 403) {
            this.isForbidden = true
            this.excludedLogins.push(this.username)
          }
        })
        .finally(() => {
          this.clearFields()
          this.sending = false
        })
    },
    clearFields() {
      this.pass = null
      this.$v.$reset()
    },
    clearError() {
      this.is401Error = this.isForbidden = false
    },
    redirect() {
      if (this.redirectUrl && this.redirectUrl !== '/') {
        this.$router.push(this.redirectUrl)
      } else {
        this.redirectToStart()
      }
    },
    redirectToStart() {
      if (this.isNeuroAdmin) {
        this.$router.push('/hospitals')
      } else {
        this.$router.push('/courses')
      }
    },
    resetPassword() {
      let self = this
      this.$v.username.$touch()
      if (!this.$v.username.$invalid) {
        this.sending = true
        this.$store
          .dispatch('SEND_EMAIL_WITH_RESET_PASSWORD', self)
          .then(response => {
            this.$emit('sent:email', response.data)
          })
          .catch(error => {
            this.surnameAdmin = this.getSurnameWithInitials(error.response.data.data.adminForThisUser)
            this.showNoEmailWarningModal()
          })
          .finally(() => {
            this.sending = false
          })
      }
    },
    goHome() {
      this.redirectToStart()
    },
    showNoEmailWarningModal() {
      this.isShowNoEmailWarningModal = true
    },
    closeNoEmailWarningModal() {
      this.isShowNoEmailWarningModal = false
    }
  }
}
</script>
